import '../map/style.css';

import { useState, useMemo } from 'react';
import { Map, APIProvider } from '@vis.gl/react-google-maps';

import { CustomAdvancedMarker } from '../map/components/custom-advanced-marker/custom-advanced-marker';

type ActivitySingleMapProps = {
  activity: any;
};

const ActivitySingleMap = ({ activity }: ActivitySingleMapProps) => {
  const [openMarkerId, setOpenMarkerId] = useState<string | null>(null);

  // Birden fazla aktivite varsa tümünü kullan, yoksa tek aktiviteyi kullan
  const activitiesToShow = useMemo(() => {
    if (
      activity?.allActivities &&
      Array.isArray(activity.allActivities) &&
      activity.allActivities.length > 1
    ) {
      return activity.allActivities;
    }
    return [activity];
  }, [activity]);

  // Tüm aktivitelerin geçerli koordinatlarını al
  const validActivities = useMemo(
    () =>
      activitiesToShow.filter((act: any) => {
        const lat = Number(act?.location?.lat);
        const lng = Number(act?.location?.long);
        return (
          !isNaN(lat) &&
          !isNaN(lng) &&
          lat !== 0 &&
          lng !== 0 &&
          lat >= -90 &&
          lat <= 90 &&
          lng >= -180 &&
          lng <= 180
        );
      }),
    [activitiesToShow]
  );

  // Harita merkezini ve zoom seviyesini hesapla
  const mapCenter = useMemo(() => {
    if (validActivities.length === 0) {
      return { lat: 41.0082, lng: 28.9784 }; // İstanbul varsayılan
    }

    if (validActivities.length === 1) {
      return {
        lat: Number(validActivities[0].location?.lat),
        lng: Number(validActivities[0].location?.long),
      };
    }

    // Birden fazla konum varsa, tüm konumları kapsayacak merkez noktasını hesapla
    const lats = validActivities.map((act: any) => Number(act.location?.lat));
    const lngs = validActivities.map((act: any) => Number(act.location?.long));

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    return {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    };
  }, [validActivities]);

  // Zoom seviyesini hesapla (birden fazla konum varsa daha geniş görünüm)
  const zoom = useMemo(() => {
    if (validActivities.length <= 1) {
      return 15;
    }

    // Birden fazla konum varsa, tüm konumları kapsayacak zoom seviyesini hesapla
    const lats = validActivities.map((act: any) => Number(act.location?.lat));
    const lngs = validActivities.map((act: any) => Number(act.location?.long));

    const latDiff = Math.max(...lats) - Math.min(...lats);
    const lngDiff = Math.max(...lngs) - Math.min(...lngs);
    const maxDiff = Math.max(latDiff, lngDiff);

    // Fark ne kadar büyükse, zoom o kadar küçük olmalı
    if (maxDiff > 0.1) return 10;
    if (maxDiff > 0.05) return 11;
    if (maxDiff > 0.02) return 12;
    if (maxDiff > 0.01) return 13;
    return 14;
  }, [validActivities]);

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string}>
      <div className="advanced-marker-example" style={{ width: '100%', height: '400px' }}>
        <Map
          mapId={process.env.REACT_APP_GOOGLE_MAPS_ID as string}
          style={{ width: '100%', height: '100%' }}
          defaultCenter={mapCenter}
          defaultZoom={zoom}
          gestureHandling="greedy"
          disableDefaultUI
          onClick={() => setOpenMarkerId(null)}
        >
          {validActivities.map((act: any) => (
            <CustomAdvancedMarker
              key={act._id || act.id}
              activity={act}
              openMarkerId={openMarkerId}
              setOpenMarkerId={setOpenMarkerId}
            />
          ))}
        </Map>
      </div>
    </APIProvider>
  );
};

export default ActivitySingleMap;
